/**
 * @author       denys.yefremov@startupsoft.co (Denys Yefremov)
 * @copyright    Copyright (c) 2022, Zumper
 */

import type { AnyAction, ThunkAction } from '@reduxjs/toolkit'
import type { SchoolDistrict, SchoolLevelsWidget, State } from '@zcode/state-type'
import { SCHOOLS_PER_PAGE, possibleLevels } from '../../components/SchoolWidget/constants'
import { clearSchoolWidgetSchools, setSelectedSchoolLevel } from '../actions/schoolWidget'
import loadSchoolWidgetDistrictSchools from './loadSchoolWidgetDistrictSchools'

export interface BuildPayloadProps {
  city: string
  level: SchoolLevelsWidget
  neighborhood?: string
  selectedDistrict: SchoolDistrict
}

export const buildPayload = ({
  city,
  level,
  neighborhood,
  selectedDistrict,
}: BuildPayloadProps) => {
  return {
    city: neighborhood ? undefined : city,
    districtId: selectedDistrict.districtId,
    neighborhood: neighborhood ? `${city}/${neighborhood}` : undefined,
    schoolLevel: level,
    schoolsLimit: SCHOOLS_PER_PAGE,
  }
}

export const loadSchools =
  (
    city: string,
    selectedDistrict: SchoolDistrict,
    neighborhood?: string
  ): ThunkAction<void, State, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(clearSchoolWidgetSchools())
    let selectedSchoolLevel: SchoolLevelsWidget

    const thunks = possibleLevels
      .filter((level) => {
        const key = `${level}Count` as const
        return selectedDistrict[key] > 0
      })
      .map((level) => {
        if (selectedSchoolLevel === undefined) {
          selectedSchoolLevel = level
          dispatch(setSelectedSchoolLevel(level))
        }
        const payload = buildPayload({
          city,
          level,
          neighborhood,
          selectedDistrict,
        })
        return dispatch(loadSchoolWidgetDistrictSchools(payload))
      })

    await Promise.all(thunks)
  }
